/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { cx, jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Hide, Inline, Show } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';

// Same colors as defined by @atlaskit/logo
const textColorMap: Record<'light' | 'dark', string> = {
	light: '#101214',
	dark: '#E2E3E4',
};

const logoStyles = cssMap({
	baseStyles: { color: token('color.text') },
	smallIconWrapper: {
		display: 'inline-block',
		flexShrink: 0,
	},
	showHideWrapper: {
		height: '24px',
	},
});
/**
 * To reduce visual changes the SSR version of this SVG will be default text colors because it's wrapped in a Text primitive.
 * After hydration the colors from the textColorMap will be applied.
 */
export const AtlassianDesignLogo: () => JSX.Element = () => {
	const { colorMode } = useThemeObserver();
	const textColor =
		typeof window === 'undefined'
			? 'currentColor'
			: colorMode
				? textColorMap[colorMode]
				: 'currentColor';

	return (
		<Inline alignBlock="center" xcss={cx(logoStyles.baseStyles)}>
			<Hide above="xs" xcss={logoStyles.showHideWrapper}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					aria-label="Atlassian Design"
					width="18"
					height="19"
					fill="none"
				>
					<path
						fill={textColor}
						d="M6.755 18.691a.477.477 0 0 1-.433.259H.488c-.346 0-.605-.39-.433-.735l4.192-8.383c.173-.346.562-.346.821-.087 2.205 2.376 2.94 6.352 1.685 8.944l.002.002ZM8.14 2.143C5.806 5.86 5.934 9.964 7.491 13.074l2.81 5.617c.087.172.26.259.433.259h5.834c.39 0 .606-.39.433-.735 0 0-7.867-15.728-8.04-16.117-.171-.303-.604-.346-.82.043l-.002.002Z"
					/>
				</svg>
			</Hide>
			<Hide above="sm" xcss={logoStyles.showHideWrapper}>
				<Show above="xs" xcss={logoStyles.showHideWrapper}>
					<svg
						aria-label="Atlassian Design"
						width="95"
						height="24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill={textColor}
							d="M6.755 18.691a.477.477 0 0 1-.433.259H.488c-.346 0-.605-.39-.433-.735l4.192-8.383c.173-.346.562-.346.821-.087 2.205 2.376 2.94 6.352 1.685 8.944l.002.002ZM8.14 2.143C5.806 5.86 5.934 9.964 7.491 13.074l2.81 5.617c.087.172.26.259.433.259h5.834c.39 0 .606-.39.433-.735 0 0-7.867-15.728-8.04-16.117-.171-.303-.604-.346-.82.043l-.002.002ZM54.864 1.86v17.09H37.769V1.86h17.095Zm-3.42 8.545a5.127 5.127 0 0 0-5.127-5.127 5.127 5.127 0 1 0 0 10.254 5.127 5.127 0 0 0 5.128-5.127ZM26.58 1.86a8.546 8.546 0 0 0 8.548 8.545A8.546 8.546 0 0 0 26.58 1.86Zm8.548 8.545a8.546 8.546 0 0 0-8.547 8.544 8.546 8.546 0 0 0 8.547-8.544Zm-8.547 8.544a8.546 8.546 0 0 0-8.548-8.544 8.546 8.546 0 0 0 8.547 8.544Zm-8.548-8.544A8.546 8.546 0 0 0 26.58 1.86a8.545 8.545 0 0 0-8.547 8.545ZM63.548 1.86v2.504l2.505 2.503 2.504-2.503V1.86h-5.009Zm5.007 17.09v-2.504l-2.504-2.503-2.504 2.503v2.503h5.008Zm1.77-16.358-1.77 1.77v3.54h3.54l1.771-1.77-3.54-3.54Zm-12.088 3.54 1.77 1.77h3.541v-3.54l-1.77-1.77-3.54 3.54ZM74.6 7.902h-2.504l-2.504 2.503 2.504 2.503H74.6V7.902Zm-.734 6.774-1.77-1.77h-3.54v3.54l1.77 1.77 3.54-3.54Zm-12.088 3.54 1.77-1.77v-3.54h-3.54l-1.77 1.77 3.54 3.54Zm-4.274-5.31h2.504l2.504-2.503L60.008 7.9h-2.504v5.007Zm8.547-5.004a2.504 2.504 0 1 0 0 5.008 2.504 2.504 0 0 0 0-5.008ZM82.299 1.86h-2.026a3.025 3.025 0 0 0-3.026 3.025c7.764.004 14.058 6.293 14.063 14.056v.001h.009a3.017 3.017 0 0 0 3.017-3.016V1.86H82.299ZM77.24 8.696v10.253h10.257c0-5.662-4.593-10.253-10.257-10.253Z"
						/>
					</svg>
				</Show>
			</Hide>
			<Show above="sm" xcss={logoStyles.showHideWrapper}>
				<svg
					aria-label="Atlassian Design"
					width="281"
					height="24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fill={textColor}
						d="M6.756 18.693a.477.477 0 0 1-.433.26H.488c-.346 0-.606-.39-.433-.736l4.193-8.384c.172-.346.562-.346.82-.087 2.206 2.377 2.94 6.353 1.686 8.946l.002.001ZM8.14 2.143C5.806 5.86 5.935 9.965 7.492 13.076l2.81 5.617c.087.172.26.26.433.26h5.835c.39 0 .606-.39.434-.736 0 0-7.869-15.73-8.04-16.12-.173-.302-.606-.345-.822.044l-.002.002ZM54.87 1.86v17.091H37.775V1.86h17.097Zm-3.418 8.546a5.127 5.127 0 0 0-5.13-5.128 5.127 5.127 0 1 0 0 10.255 5.128 5.128 0 0 0 5.13-5.127ZM26.584 1.86a8.547 8.547 0 0 0 8.548 8.546 8.547 8.547 0 0 0-8.548-8.546Zm8.548 8.546a8.547 8.547 0 0 0-8.548 8.545 8.547 8.547 0 0 0 8.548-8.546Zm-8.548 8.545a8.547 8.547 0 0 0-8.549-8.546 8.547 8.547 0 0 0 8.549 8.546Zm-8.549-8.546a8.547 8.547 0 0 0 8.549-8.545 8.546 8.546 0 0 0-8.549 8.546ZM63.557 1.86v2.504l2.504 2.503 2.505-2.503V1.86h-5.01Zm5.007 17.091v-2.504l-2.505-2.503-2.504 2.503v2.504H68.564Zm1.77-16.359-1.77 1.77v3.54h3.54l1.772-1.77-3.542-3.54Zm-12.089 3.54 1.77 1.77h3.542v-3.54l-1.77-1.77-3.542 3.54Zm16.364 1.77h-2.504L69.6 10.405l2.505 2.504h2.504V7.902Zm-.733 6.775-1.771-1.77h-3.541v3.54l1.77 1.77 3.541-3.54Zm-12.09 3.54 1.77-1.77v-3.54h-3.54l-1.771 1.77 3.541 3.54Zm-4.275-5.31h2.505l2.504-2.503L60.015 7.9h-2.504v5.008Zm8.548-5.005a2.505 2.505 0 1 0 0 5.01 2.505 2.505 0 0 0 0-5.01ZM82.31 1.86h-2.026a3.026 3.026 0 0 0-3.026 3.025c7.765.004 14.06 6.294 14.065 14.057v.002h.008a3.018 3.018 0 0 0 3.018-3.016V1.86H82.31Zm-5.058 6.836v10.255H87.51c0-5.663-4.593-10.255-10.258-10.255ZM110.802 15.421c-1.197 0-2.394-.131-3.721-.395l-1.198 3.684h-3.435l6.324-17.289h4.216l6.299 17.29h-3.436l-1.197-3.685c-1.379.264-2.602.395-3.852.395Zm0-2.579c.963 0 1.926-.079 3.045-.263L110.88 3.5l-2.993 9.105c1.067.158 2.004.237 2.915.237ZM123.301 14.263c0 1.08.624 1.763 1.848 1.763.494 0 .937-.079 1.301-.158v2.737c-.468.105-.989.21-1.796.21-2.941 0-4.242-1.736-4.242-4.315V8.026h-1.874V5.421h1.874v-2.79h2.889v2.79h3.149v2.605h-3.149v6.237ZM133.563 18.658c-.208.079-.651.131-1.327.131-2.291 0-3.748-1.079-3.748-3.684V.053h2.941v14.684c0 1.026.677 1.395 1.509 1.395.235 0 .365 0 .625-.027v2.553ZM137.043 12.079c0 2.816 1.093 4.237 3.097 4.237 1.717 0 3.279-1.132 3.279-3.71v-1.08c0-2.579-1.406-3.71-3.071-3.71-2.186 0-3.305 1.5-3.305 4.263Zm6.376 6.631v-2.394c-.807 1.763-2.342 2.658-4.268 2.658-3.384 0-5.023-2.974-5.023-6.895 0-3.763 1.717-6.921 5.283-6.921 1.848 0 3.253.947 4.008 2.631V5.421h2.941v13.29h-2.941ZM158.243 14.974c0 2.342-1.457 4-5.075 4-2.03 0-3.696-.474-4.711-1v-2.816c1.145.658 3.097 1.29 4.789 1.29 1.458 0 2.238-.606 2.238-1.474 0-.842-.676-1.342-2.811-1.842-3.149-.71-4.346-1.974-4.346-4.106 0-2.42 1.822-3.868 4.919-3.868 1.77 0 3.409.42 4.398.974v2.736c-1.613-.736-3.019-1.184-4.424-1.184-1.431 0-2.186.474-2.186 1.342 0 .816.546 1.29 2.707 1.79 3.175.71 4.502 1.92 4.502 4.158ZM169.414 14.974c0 2.342-1.458 4-5.075 4-2.03 0-3.696-.474-4.711-1v-2.816c1.145.658 3.097 1.29 4.789 1.29 1.457 0 2.238-.606 2.238-1.474 0-.842-.677-1.342-2.811-1.842-3.149-.71-4.346-1.974-4.346-4.106 0-2.42 1.822-3.868 4.919-3.868 1.77 0 3.409.42 4.398.974v2.736c-1.613-.736-3.019-1.184-4.424-1.184-1.432 0-2.186.474-2.186 1.342 0 .816.546 1.29 2.706 1.79 3.176.71 4.503 1.92 4.503 4.158ZM170.721 1.868c0-1.184.833-1.868 1.952-1.868s1.952.684 1.952 1.868c0 1.185-.833 1.895-1.952 1.895s-1.952-.71-1.952-1.895Zm.468 16.842V5.421h2.941v13.29h-2.941ZM178.828 12.079c0 2.816 1.093 4.237 3.097 4.237 1.718 0 3.28-1.132 3.28-3.71v-1.08c0-2.579-1.406-3.71-3.071-3.71-2.187 0-3.306 1.5-3.306 4.263Zm6.377 6.631v-2.394c-.807 1.763-2.343 2.658-4.269 2.658-3.383 0-5.023-2.974-5.023-6.895 0-3.763 1.718-6.921 5.284-6.921 1.847 0 3.253.947 4.008 2.631V5.421h2.941v13.29h-2.941ZM202.163 10.842v7.868h-2.941v-8c0-2.052-.807-2.947-2.629-2.947-1.795 0-3.019 1.21-3.019 3.553v7.394h-2.941V5.421h2.941v2.21c.807-1.578 2.265-2.473 4.034-2.473 2.967 0 4.555 2.079 4.555 5.684ZM215.328 4.316h-3.383v11.5h3.513c3.488 0 5.231-1.869 5.231-5.658 0-3.842-1.665-5.842-5.361-5.842Zm-6.506 14.395V1.42h6.61c5.648 0 8.354 3.5 8.354 8.684 0 5.237-2.732 8.605-8.354 8.605h-6.61ZM231.214 7.579c-2.082 0-3.123 1.368-3.332 3.526h6.377c-.13-2.29-1.119-3.526-3.045-3.526Zm5.205 10.605c-1.119.553-2.785.79-4.216.79-5.023 0-7.209-2.71-7.209-6.869 0-4.052 2.212-6.947 6.298-6.947 4.138 0 5.804 2.868 5.804 6.947v1.027h-9.188c.313 2.026 1.614 3.184 4.373 3.184 1.535 0 2.915-.29 4.138-.684v2.552ZM248.321 14.974c0 2.342-1.457 4-5.075 4-2.03 0-3.696-.474-4.711-1v-2.816c1.145.658 3.097 1.29 4.789 1.29 1.458 0 2.238-.606 2.238-1.474 0-.842-.676-1.342-2.81-1.842-3.15-.71-4.347-1.974-4.347-4.106 0-2.42 1.822-3.868 4.919-3.868 1.77 0 3.41.42 4.398.974v2.736c-1.613-.736-3.019-1.184-4.424-1.184-1.431 0-2.186.474-2.186 1.342 0 .816.546 1.29 2.707 1.79 3.175.71 4.502 1.92 4.502 4.158ZM249.628 1.868c0-1.184.833-1.868 1.952-1.868s1.952.684 1.952 1.868c0 1.185-.833 1.895-1.952 1.895s-1.952-.71-1.952-1.895Zm.469 16.842V5.421h2.94v13.29h-2.94ZM264.06 17.5v-1.368c-.807 1.763-2.343 2.631-4.268 2.631-3.332 0-4.971-2.92-4.971-6.79 0-3.71 1.691-6.815 5.231-6.815 1.848 0 3.253.947 4.008 2.605V5.421h2.915v11.921c0 3.974-1.9 6.658-6.793 6.658-2.082 0-3.461-.29-4.763-.71V20.5c1.458.526 2.941.816 4.581.816 3.019 0 4.06-1.605 4.06-3.816Zm-6.377-5.526c0 2.79 1.094 4.184 3.098 4.184 1.769 0 3.279-1.158 3.279-3.763V11.5c0-2.579-1.38-3.71-3.045-3.71-2.186 0-3.332 1.5-3.332 4.184ZM280.969 10.842v7.868h-2.941v-8c0-2.052-.807-2.947-2.629-2.947-1.796 0-3.019 1.21-3.019 3.553v7.394h-2.941V5.421h2.941v2.21c.807-1.578 2.264-2.473 4.034-2.473 2.967 0 4.555 2.079 4.555 5.684Z"
					/>
				</svg>
			</Show>
		</Inline>
	);
};

/**
 * A small (icon-only) Atlassian Design logo.
 *
 * Uses the same light/dark theming behavior as `AtlassianDesignLogo`.
 */
export const AtlassianDesignLogoSmall: ({ size }: { size?: number }) => React.ReactNode = ({
	size = 24,
}: {
	size?: number;
}): React.ReactNode => {
	const { colorMode } = useThemeObserver();
	const iconColor =
		typeof window === 'undefined'
			? 'currentColor'
			: colorMode
				? textColorMap[colorMode]
				: 'currentColor';

	return (
		<Inline alignBlock="center" xcss={cx(logoStyles.baseStyles)}>
			<span
				css={logoStyles.smallIconWrapper}
				aria-label="Good Design Award - gold winner"
				style={{
					width: size,
					height: size,
				}}
			>
				<svg
					width="30"
					height="20"
					viewBox="0 0 30 20"
					aria-label="Atlassian Design"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M5.62997 16.0278C5.5575 16.1712 5.41402 16.2437 5.26908 16.2437H0.40646C0.118036 16.2437 -0.0979195 15.9191 0.0455677 15.6308L3.53999 8.64425C3.68347 8.35592 4.00813 8.35592 4.22409 8.5718C6.06188 10.5524 6.67351 13.8661 5.62852 16.0263L5.62997 16.0278ZM6.78367 2.23581C4.83862 5.33353 4.94587 8.75436 6.24305 11.3464C7.54023 13.9399 8.51276 15.8844 8.58523 16.0278C8.65769 16.1712 8.80118 16.2437 8.94612 16.2437H13.8087C14.1334 16.2437 14.3131 15.9191 14.1696 15.6308C14.1696 15.6308 7.6127 2.52269 7.46921 2.19814C7.32573 1.94603 6.96484 1.90981 6.78512 2.23436L6.78367 2.23581Z"
						fill={iconColor}
					/>
				</svg>
			</span>
		</Inline>
	);
};
